// Relay.link API client for cross-chain USDC withdrawals

const RELAY_BASE_URL = 'https://api.relay.link'

// Chain configurations
export const CHAIN_CONFIG = {
	polygon: {
		id: 137,
		name: 'Polygon',
		usdc: '0x2791Bca1f2de4661ED88A30C99A7a9449Aa84174', // USDC.e
		explorer: 'https://polygonscan.com',
	},
	solana: {
		id: 792703809,
		name: 'Solana',
		usdc: 'EPjFWdd5AufqSSqeM2qN1xzybapC8G4wEGGkZwyTDt1v',
		explorer: 'https://solscan.io',
	},
} as const

export type SupportedChainId = 137 | 792703809

// API Types

export type RelayQuoteParams = {
	user: string
	originChainId: number
	originCurrency: string
	destinationChainId: number
	destinationCurrency: string
	amount: string
	recipient?: string
	tradeType?: 'EXACT_INPUT' | 'EXACT_OUTPUT'
}

export type RelayMultiInputOrigin = {
	chainId: number
	currency: string
	amount: string
	user?: string
}

export type RelayMultiInputParams = {
	user: string
	origins: RelayMultiInputOrigin[]
	destinationChainId: number
	destinationCurrency: string
	recipient?: string
	tradeType?: 'EXACT_INPUT' | 'EXACT_OUTPUT'
}

export type RelayFee = {
	currency: {
		chainId: number
		address: string
		symbol: string
		name: string
		decimals: number
	}
	amount: string
	amountFormatted: string
	amountUsd: string
}

export type RelayStep = {
	id: string
	action: string
	description: string
	kind: 'transaction' | 'signature'
	requestId?: string
	items: Array<{
		status: 'incomplete' | 'complete'
		data?: {
			chainId?: number
			to?: string
			from?: string
			data?: string
			value?: string
			maxFeePerGas?: string
			maxPriorityFeePerGas?: string
			gas?: number
			// Solana instructions
			instructions?: Array<{
				keys: Array<{ pubkey: string; isSigner: boolean; isWritable: boolean }>
				programId: string
				data: string
			}>
		}
		check?: {
			endpoint: string
			method: string
		}
	}>
}

export type RelayQuoteDetails = {
	sender: string
	recipient: string
	currencyIn: {
		currency: {
			chainId: number
			address: string
			symbol: string
			name: string
			decimals: number
		}
		amount: string
		amountFormatted: string
		amountUsd: string
	}
	currencyOut: {
		currency: {
			chainId: number
			address: string
			symbol: string
			name: string
			decimals: number
		}
		amount: string
		amountFormatted: string
		amountUsd: string
	}
	timeEstimate?: number
	totalImpact?: {
		usd: string
		percent: string
	}
}

export type RelayQuoteResponse = {
	steps: RelayStep[]
	fees: {
		gas?: RelayFee
		relayer?: RelayFee
		relayerGas?: RelayFee
		relayerService?: RelayFee
		app?: RelayFee
	}
	details: RelayQuoteDetails
}

export type RelayStatusResponse = {
	requestId: string
	status: 'pending' | 'success' | 'failure' | 'refund'
	user: string
	recipient: string
	data?: {
		inTxHashes?: string[]
		outTxHashes?: string[]
		failReason?: string
	}
	originChainId: number
	destinationChainId: number
	createdAt: string
	updatedAt: string
}

// API Functions

/**
 * Get a quote for a single-origin swap/bridge
 */
export async function getRelayQuote(params: RelayQuoteParams): Promise<RelayQuoteResponse> {
	const res = await fetch(`${RELAY_BASE_URL}/quote/v2`, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
		},
		body: JSON.stringify(params),
	})

	if (!res.ok) {
		const error = await res.json().catch(() => ({ message: res.statusText }))
		throw new Error(error.message || `Relay API error: ${res.status}`)
	}

	return res.json()
}

/**
 * Get a quote for multi-origin swap (combining balances from multiple chains)
 */
export async function getRelayMultiInputQuote(
	params: RelayMultiInputParams
): Promise<RelayQuoteResponse> {
	const res = await fetch(`${RELAY_BASE_URL}/execute/swap/multi-input`, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
		},
		body: JSON.stringify(params),
	})

	if (!res.ok) {
		const error = await res.json().catch(() => ({ message: res.statusText }))
		throw new Error(error.message || `Relay API error: ${res.status}`)
	}

	return res.json()
}

/**
 * Get the status of a relay transaction
 */
export async function getRelayStatus(requestId: string): Promise<RelayStatusResponse> {
	const res = await fetch(
		`${RELAY_BASE_URL}/intents/status/v3?requestId=${encodeURIComponent(requestId)}`,
		{
			method: 'GET',
			headers: {
				'Content-Type': 'application/json',
			},
		}
	)

	if (!res.ok) {
		const error = await res.json().catch(() => ({ message: res.statusText }))
		throw new Error(error.message || `Relay API error: ${res.status}`)
	}

	return res.json()
}

// Helper Functions

/**
 * Get the USDC address for a given chain ID
 */
export function getUsdcAddress(chainId: number): string {
	if (chainId === CHAIN_CONFIG.polygon.id) {
		return CHAIN_CONFIG.polygon.usdc
	}
	if (chainId === CHAIN_CONFIG.solana.id) {
		return CHAIN_CONFIG.solana.usdc
	}
	throw new Error(`Unsupported chain ID: ${chainId}`)
}

/**
 * Get the block explorer URL for a transaction
 */
export function getExplorerUrl(chainId: number, txHash: string): string {
	if (chainId === CHAIN_CONFIG.polygon.id) {
		return `${CHAIN_CONFIG.polygon.explorer}/tx/${txHash}`
	}
	if (chainId === CHAIN_CONFIG.solana.id) {
		return `${CHAIN_CONFIG.solana.explorer}/tx/${txHash}`
	}
	return ''
}
